import { DownloadOutlined, EyeOutlined } from '@ant-design/icons';
import {
  getGetFrontRichiesteIdAllegatiIdDownloadQueryKey,
  getGetFrontRichiesteIdAllegatiIdDownloadQueryOptions,
} from '@framework/api-client/api/inquiries-compile/richieste-controller/richieste-controller.ts';
import { getNetworkErrorMessage } from '@framework/auth/http/errors/getNetworkErrorMessage.ts';
import { useAuthDownload } from '@framework/common-web/utils/download/useAuthDownload.ts';
import { Button, message, Tooltip } from 'antd';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

interface IDownloadAttachmentButtonProps {
  submissionId: string;
  attachmentId: string;
  disabled?: boolean;
  /** Se true, apre il file nel viewer inline */
  viewInline?: boolean;
  /** Suffisso per differenziare la query key quando più istanze condividono lo stesso endpoint */
  queryKeySuffix?: string;
}

/**
 * Pulsante solo-icona per download o visualizzazione inline di un allegato.
 * Speculare a DownloadModuleButton per i moduli.
 */
export function DownloadAttachmentButton(
  props: IDownloadAttachmentButtonProps,
) {
  const { submissionId, attachmentId, disabled, viewInline, queryKeySuffix } =
    props;
  const { t } = useTranslation();

  const { download, isFetching } = useAuthDownload(
    getGetFrontRichiesteIdAllegatiIdDownloadQueryOptions,
    [submissionId, attachmentId],
    {
      onError: e => {
        console.error('[DownloadAttachmentButton] Error: ', { error: e });
        message.error(
          getNetworkErrorMessage(
            e,
            t(
              'submission-summary.errore-download-allegato',
              'Si è verificato un problema durante il download del file',
            ),
          ),
        );
      },
      showInlineViewer: viewInline ?? false,
      ...(queryKeySuffix && {
        queryOptions: {
          queryKey: [
            ...getGetFrontRichiesteIdAllegatiIdDownloadQueryKey(
              submissionId,
              attachmentId,
            ),
            queryKeySuffix,
          ],
        },
      }),
    },
  );

  const handleClick = useCallback(() => {
    if (submissionId && attachmentId && !disabled) {
      download();
    }
  }, [download, disabled, submissionId, attachmentId]);

  const icon = viewInline ? (
    <EyeOutlined
      aria-label={t('submission-summary.visualizza', 'Visualizza')}
    />
  ) : (
    <DownloadOutlined aria-label={t('submission-summary.scarica', 'Scarica')} />
  );

  const text = viewInline
    ? t('submission-summary.visualizza', 'Visualizza')
    : t('submission-summary.scarica', 'Scarica');

  return (
    <Tooltip
      title={
        disabled
          ? t(
              'submission-summary.allegato-non-disponibile',
              'File non disponibile',
            )
          : text
      }
    >
      <Button
        icon={icon}
        onClick={handleClick}
        disabled={!submissionId || !attachmentId || disabled}
        loading={isFetching}
        size="small"
        type="link"
      />
    </Tooltip>
  );
}
